import { useContext } from 'react'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCog, faUserShield, faUser } from '@fortawesome/pro-light-svg-icons'

import { generateRedirectUrl } from '@domain-group/javascript-utils'

import {
  Box, DashboardModuleListItem, IconWithMenu, ListItem, Link, Text,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useBrandGroupMember from '@hooks/useBrandGroupMember'
import { generateUrls } from '@hooks/useAgency'

import type { BrandGroupMemberModel } from '@models/types'

const confirmDelete = (
  deleteBrandGroupMember: (payload: HandleCallbackActionParams) => Promise<object>,
  deleteFn: () => void,
) => {
  const deleteBrandGroupMemberPayload = {
    callbacks: {
      action: deleteFn,
    },
    toastText: 'Member Removed',
  }

  swal
    .fire({
      confirmButtonColor: '#DD6B55',
      confirmButtonText: 'Yes, remove it.',
      icon: 'warning',
      showCancelButton: true,
      text: 'Are you sure? It cannot be recovered.',
      title: 'Remove Member?',
    })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2345
        deleteBrandGroupMember(deleteBrandGroupMemberPayload)
      }
    })
}

const confirmSetAdmin = (
  setAdminBrandGroupMember: (payload: HandleCallbackActionParams) => Promise<object>,
  setAdminFn: () => void,
) => {
  const setAdminBrandGroupMemberPayload = {
    callbacks: {
      action: setAdminFn,
    },
    toastText: 'New Admin Set',
  }

  swal
    .fire({
      confirmButtonColor: '#DD6B55',
      confirmButtonText: 'Yes, set it.',
      icon: 'warning',
      showCancelButton: true,
      text: 'Are you sure?',
      title: 'Set this Member as Admin?',
    })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2345
        setAdminBrandGroupMember(setAdminBrandGroupMemberPayload)
      }
    })
}

const BrandGroupMemberListItem = (props: { brandGroupMember: BrandGroupMemberModel }) => {
  const { brandGroupMember: initBrandGroupMember } = props

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      deleteBrandGroupMember,
      setAdminBrandGroupMember,
    },
  } = pageContext

  const {
    brandGroupMember: {
      admin,
      member,
    },
    callbacks: {
      deleteBrandGroupMember: deleteFn,
      setAdmin: setAdminFn,
    },
  } = useBrandGroupMember(initBrandGroupMember)

  // @ts-expect-error TS2339
  const { id, title } = member

  // @ts-expect-error TS18048
  const { editAgencyUrl } = generateUrls({ id: member.id })

  return (
    <ListItem
      boxProps={{
        alignItems: 'center',
        borderBottom: '1px dashed',
        fontSize: 'small',
        padding: 'large',
      }}
      disableHover
    >
      <Box
        flexDirection="column"
        maxWidth="80%"
        paddingRight="large"
      >
        <Text variant="ellipsis">
          {title}
        </Text>

        <Box
          color="bodyFontLightColor"
          fontSize="xsmall"
          marginTop="small"
        >
          <FontAwesomeIcon icon={admin ? faUserShield : faUser} />
          <Text color="bodyFontLightColor" marginLeft="small">
            {admin ? 'Admin' : 'Member'}
          </Text>
        </Box>
      </Box>

      <Box justifyContent="flex-end">
        <Text color="bodyFontLightColor" marginRight="large">
          # {id}
        </Text>

        <IconWithMenu
          boxProps={{ marginRight: 'medium' }}
          icon={(
            <Link
              greyLink
              disabled={admin}
            >
              <FontAwesomeIcon icon={faCog} />
            </Link>
          )}
          shouldUsePortal
        >
          {/*
           // @ts-expect-error TS2741 */}
          <IconWithMenu.Menu closeOnClick listStyle={{ right: '-22px' }}>
            <Link key="admin" onClick={() => confirmSetAdmin(setAdminBrandGroupMember, setAdminFn)}>
              <FontAwesomeIcon icon={faUserShield} /> Set as Admin
            </Link>
          </IconWithMenu.Menu>
        </IconWithMenu>

        {/*
         // @ts-expect-error TS2339 */}
        <DashboardModuleListItem.Icon
          marginRight="medium"
          onClick={() => confirmDelete(deleteBrandGroupMember, deleteFn)}
          type="delete"
        />

        {/*
         // @ts-expect-error TS2339 */}
        <DashboardModuleListItem.Icon
          href={`${editAgencyUrl}?redirect=${generateRedirectUrl()}`}
          type="link"
        />
      </Box>
    </ListItem>
  )
}

export default BrandGroupMemberListItem
